import { Action, AsyncState, unloadedAsyncState } from "widgets-for-react";
import { Map, Set } from 'immutable'
import { Means } from "../LocationPageState";
import { initialTimeslotSelection, TimeslotAvailability, TimeslotSelection } from "./TimeslotInput/TimeslotInputState";
import { OvaticLocation } from "../../LocationState";
import { moment } from "../../../../../custom_utils/moment";
import { validateField, ValidatorProps } from "./LocationOptionFormValidation";

export interface LocationOptionFormProps {
  locationId: number
  location: AsyncState<OvaticLocation>
  means: AsyncState<Means[]>
}

export type PartsOfDay = 'morning' | 'afternoon' | 'evening';

export interface LocationOptionData {
  firstName: string
  infix: string
  lastName: string
  companyName: string
  email: string
  phoneNumber: string
  date: Date
  seatplanId: number
  participants: number
  timeslots: TimeslotSelection
  means: Map<MeansId, number>
  note: string
}

export interface Message {
  kind: "success" | "error"
  message: string
}

export interface LocationOptionFormState {
  data: LocationOptionData
  visited: Set<Field>
  validationMessages: Map<Field, string>
  message?: Message
  expandedMeansCategories: Set<string>
  submitting: boolean
  monthlyAvailabilities: Map<string, Map<string, TimeslotAvailability>>
}

export const minimumRentalDate = moment().add(1, 'weeks').toDate()

const initialLocationOptionData: LocationOptionData = {
  firstName: "",
  infix: "",
  lastName: "",
  companyName: "",
  email: "",
  phoneNumber: "",
  date: minimumRentalDate,
  seatplanId: undefined,
  participants: 0,
  timeslots: initialTimeslotSelection,
  means: Map<MeansId, number>(),
  note: "",
}

const testLocationOptionData: () => LocationOptionData = () => ({
  firstName: "Andra",
  infix: "ver",
  lastName: "Aart",
  companyName: "Hoppinger",
  email: `andraveraart+dnb-${Date.now().toString().slice(6)}@hoppinger.com`,
  phoneNumber: "0632165498",
  date: minimumRentalDate,
  seatplanId: (() => { console.error("WARNING: TEST DATA BEING USED FOR FORM"); return undefined })(),
  participants: 42,
  timeslots: initialTimeslotSelection,
  means: Map<MeansId, number>(),
  note: (new Date()).toISOString()
})

export const initialState: LocationOptionFormState = {
  data: initialLocationOptionData,
  visited: Set<Field>(),
  validationMessages: Map<Field, string>(),
  message: undefined,
  expandedMeansCategories: Set<string>(),
  submitting: false,
  monthlyAvailabilities: Map<string, Map<string, TimeslotAvailability>>(),
}

export type MeansId = number

export const setMeans = (means: LocationOptionData['means']) =>
  (id: MeansId, amount: number) => amount <= 0
    ? means.remove(id)
    : means.set(id, amount)

export type Field = keyof LocationOptionData
export const allFields = Set(Object.keys(initialLocationOptionData) as Array<Field>)

export const setVisited =
  (setState: (action: Action<LocationOptionFormState>) => void) => (field: Field) =>
    setState((s) => ({ ...s, visited: s.visited.add(field) }))

export const validateFormData =
  (setState: (action: Action<LocationOptionFormState>) => void, validatorProps: ValidatorProps) =>
    (visited: Set<Field>) => setState((prevState) => {
      const validationMessages = validateFields(visited, prevState.data, validatorProps)
      return { ...prevState, validationMessages, visited }
    })

export const validateFields = (fields: Set<Field>, data: LocationOptionData, validatorProps: ValidatorProps) => fields.reduce<Map<Field, string>>((messages, field) => {
  const result = validateField(validatorProps)(field)(data[field])
  return result.status == 'invalid'
    ? messages.set(field, result.message)
    : messages
}, Map<Field, string>())

export const setFieldOnState =
  (setState: (action: Action<LocationOptionFormState>, callback?: () => void) => void) =>
    <Field extends keyof LocationOptionData>(field: Field) =>
      (value: LocationOptionData[Field], callback?: () => void) =>
        setState(
          (s) => ({ ...s, data: { ...s.data, [field]: value } }),
          () => callback && callback()
        )

export interface OvaticReservationRequest {
  LocationId: number
  LocationName: string
  SeatplanId: number
  SeatplanName: string
  StartDate: string
  EndDate: string
  Email: string
  PhoneNumber: string
  FirstName: string
  Infix: string
  LastName: string
  CompanyName: string
  Participants: number
  ExtraRemarks: string
}